import { Text, cn } from '@billda/design-system';
import {
  getProgressTone,
  progressToneClasses,
} from '../../../features/sites/components/v2/atoms/ProgressBar';

interface ProgressChipProps {
  percentage: number | null;
}

const RING_SIZE = 16;
const RING_STROKE = 4;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export function ProgressChip({ percentage }: ProgressChipProps) {
  if (percentage == null) {
    return null;
  }

  const clamped = Math.max(0, Math.min(percentage, 100));
  const tone = getProgressTone(clamped);

  return (
    <div className="hidden w-[54px] shrink-0 items-center gap-1.5 sm:flex">
      {/* Ring — pinned left */}
      <span
        className="relative shrink-0"
        style={{ width: RING_SIZE, height: RING_SIZE }}
      >
        <svg
          aria-hidden="true"
          width={RING_SIZE}
          height={RING_SIZE}
          viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          className="-rotate-90"
        >
          {/* Track */}
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            strokeWidth={RING_STROKE}
            className="stroke-border-strong"
          />
          {/* Fill — tone colour from the shared progress helpers */}
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            strokeWidth={RING_STROKE}
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={RING_CIRCUMFERENCE * (1 - clamped / 100)}
            strokeLinecap="round"
            className={cn('stroke-current', progressToneClasses[tone])}
          />
        </svg>
      </span>

      {/* Percentage — fills remaining space, right-aligned */}
      <Text
        variant="body2"
        className="flex-1 text-right font-semibold leading-none tabular-nums"
        color="secondary"
      >
        {Math.round(clamped)}%
      </Text>
    </div>
  );
}
